import { useState, type FormEvent } from "react";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { loginError, useAuth } from "./auth";
import { t } from "@/lib/i18n";

/** Вход только через Google. Пускаем почты, которые владелец или администратор добавил в «Настройках пользователей». */
export default function LoginPanel({ align = "start" }: { align?: "start" | "center" }) {
  const { loading, config, signInWithGoogle, devLogin } = useAuth();
  const [busy, setBusy] = useState(false);
  const [devEmail, setDevEmail] = useState("");
  const [error, setError] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return loginError(q.get("error"), q.get("email"));
  });

  const centered = align === "center" ? "items-center text-center" : "items-start";

  const onGoogle = () => {
    if (busy) return;
    setBusy(true);
    setError("");
    signInWithGoogle();
  };

  const onDev = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError((await devLogin(devEmail.trim())) || "");
  };

  return (
    <div className={`flex w-full max-w-md flex-col gap-4 ${centered}`}>
      <button
        type="button"
        onClick={onGoogle}
        disabled={busy || loading || config?.google === false}
        className="flash-btn group inline-flex h-12 w-full items-center justify-center gap-3 rounded-btn bg-card px-6 text-base font-semibold text-foreground ring-1 ring-border transition hover:ring-primary/60 disabled:opacity-60"
      >
        <span className="grid size-7 flex-none place-items-center rounded-full bg-primary/15 text-primary">
          {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <i className="ri-google-fill text-base leading-none" aria-hidden />}
        </span>
        <span className="whitespace-nowrap">{busy ? t("Открываем Google…") : t("Войти через Google")}</span>
        {!busy ? <ArrowRight className="size-4 text-muted-foreground transition group-hover:translate-x-0.5" aria-hidden /> : null}
      </button>

      {config?.google === false ? (
        <p role="status" className="w-full rounded-btn border border-border bg-muted px-4 py-2.5 text-left text-sm text-foreground">
          {t("Вход через Google ещё не настроен на сервере.")}
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="w-full rounded-btn border border-primary/40 bg-primary/10 px-4 py-2.5 text-left text-sm text-foreground">
          {error}
        </p>
      ) : null}

      <p className="flex w-full items-start gap-2 text-left text-xs leading-relaxed text-muted-foreground">
        <ShieldCheck className="mt-0.5 size-3.5 flex-none text-primary" aria-hidden />
        {t("Доступ только по приглашению: войти можно с той почтой Google, которую владелец или администратор добавил в «Настройках пользователей».")}
      </p>

      {config?.devLogin ? (
        <form onSubmit={onDev} className="flex w-full flex-col gap-2 rounded-card border border-dashed border-border p-3 text-left">
          <span className="text-xs font-semibold text-muted-foreground">{t("Локальная разработка: вход по почте без Google")}</span>
          <div className="flex gap-2">
            <input
              type="email"
              value={devEmail}
              onChange={(e) => setDevEmail(e.target.value)}
              placeholder="owner@hub.test"
              className="h-10 min-w-0 flex-1 rounded-btn border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary/60 focus:outline-none"
            />
            <button type="submit" className="h-10 whitespace-nowrap rounded-btn bg-primary px-4 text-sm font-semibold text-primary-foreground">
              {t("Войти")}
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
